import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, FilterIcon, DownloadIcon, EyeIcon, EditIcon, TrashIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import StatusBadge from '../../components/ui/StatusBadge';
import Modal from '../../components/ui/Modal';
// Mock user data
const usersData = [{
  id: 1,
  fullName: 'John Smith',
  username: 'johnsmith',
  email: 'john@example.com',
  phone: '+1 (555) 123-4567',
  status: 'active',
  role: 'Admin',
  region: 'North America',
  lastLogin: '2023-07-15T10:30:00Z'
}, {
  id: 2,
  fullName: 'Jane Doe',
  username: 'janedoe',
  email: 'jane@example.com',
  phone: '+1 (555) 987-6543',
  status: 'active',
  role: 'Manager',
  region: 'Europe',
  lastLogin: '2023-07-16T14:45:00Z'
}, {
  id: 3,
  fullName: 'Robert Johnson',
  username: 'robertj',
  email: 'robert@example.com',
  phone: '+1 (555) 456-7890',
  status: 'inactive',
  role: 'Agent',
  region: 'Asia',
  lastLogin: '2023-07-10T08:15:00Z'
}, {
  id: 4,
  fullName: 'Sarah Williams',
  username: 'sarahw',
  email: 'sarah@example.com',
  phone: '+1 (555) 234-5678',
  status: 'active',
  role: 'Auditor',
  region: 'South America',
  lastLogin: '2023-07-17T09:20:00Z'
}, {
  id: 5,
  fullName: 'Michael Brown',
  username: 'michaelb',
  email: 'michael@example.com',
  phone: '+1 (555) 876-5432',
  status: 'locked',
  role: 'Agent',
  region: 'Africa',
  lastLogin: '2023-07-05T16:30:00Z'
}];
interface User {
  id: number;
  fullName: string;
  username: string;
  email: string;
  phone: string;
  status: string;
  role: string;
  region: string;
  lastLogin: string;
}
const UserList: React.FC = () => {
  const navigate = useNavigate();
  const [users] = useState<User[]>(usersData);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const handleAddUser = () => {
    navigate('/users/new');
  };
  const handleEditUser = (user: User) => {
    navigate(`/users/edit/${user.id}`);
  };
  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };
  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };
  const confirmDelete = () => {
    // Delete logic would go here
    setShowDeleteModal(false);
    setSelectedUser(null);
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <StatusBadge status="success" text="Active" data-id="element-6919" />;
      case 'inactive':
        return <StatusBadge status="danger" text="Inactive" data-id="element-6920" />;
      case 'locked':
        return <StatusBadge status="warning" text="Locked" data-id="element-6921" />;
      default:
        return <StatusBadge status="neutral" text={status} data-id="element-6922" />;
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-6923" />;
      case 'inactive':
      case 'locked':
        return <XCircleIcon className="h-5 w-5 text-red-500" data-id="element-6924" />;
      default:
        return null;
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };
  const columns = [{
    header: 'Full Name',
    accessor: 'fullName',
    sortable: true
  }, {
    header: 'Username',
    accessor: 'username',
    sortable: true
  }, {
    header: 'Email',
    accessor: 'email',
    sortable: true
  }, {
    header: 'Role',
    accessor: 'role',
    sortable: true
  }, {
    header: 'Status',
    accessor: (user: User) => getStatusBadge(user.status),
    sortable: false
  }, {
    header: 'Region',
    accessor: 'region',
    sortable: true
  }, {
    header: 'Last Login',
    accessor: (user: User) => formatDate(user.lastLogin),
    sortable: true
  }];
  return <div className="space-y-6" data-id="element-6925">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-6926">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-6927">
          User Management
        </h1>
        <div className="mt-3 sm:mt-0 flex flex-wrap gap-2" data-id="element-6928">
          <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-6929">
            <FilterIcon className="h-4 w-4 mr-2" data-id="element-6930" />
            Filters
          </button>
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-6931">
            <DownloadIcon className="h-4 w-4 mr-2" data-id="element-6932" />
            Export
          </button>
          <button onClick={handleAddUser} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-6933">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-6934" />
            Add User
          </button>
        </div>
      </div>
      {showFilters && <Card data-id="element-6935">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-6936">
            <div data-id="element-6937">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6938">
                Status
              </label>
              <select className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-6939">
                <option value="" data-id="element-6940">All Statuses</option>
                <option value="active" data-id="element-6941">Active</option>
                <option value="inactive" data-id="element-6942">Inactive</option>
                <option value="locked" data-id="element-6943">Locked</option>
              </select>
            </div>
            <div data-id="element-6944">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6945">
                Role
              </label>
              <select className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-6946">
                <option value="" data-id="element-6947">All Roles</option>
                <option value="Admin" data-id="element-6948">Admin</option>
                <option value="Manager" data-id="element-6949">Manager</option>
                <option value="Agent" data-id="element-6950">Agent</option>
                <option value="Auditor" data-id="element-6951">Auditor</option>
              </select>
            </div>
            <div data-id="element-6952">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6953">
                Region
              </label>
              <select className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-6954">
                <option value="" data-id="element-6955">All Regions</option>
                <option value="North America" data-id="element-6956">North America</option>
                <option value="Europe" data-id="element-6957">Europe</option>
                <option value="Asia" data-id="element-6958">Asia</option>
                <option value="South America" data-id="element-6959">South America</option>
                <option value="Africa" data-id="element-6960">Africa</option>
                <option value="Oceania" data-id="element-6961">Oceania</option>
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-3" data-id="element-6962">
            <button className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-6963">
              Reset
            </button>
            <button className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-6964">
              Apply Filters
            </button>
          </div>
        </Card>}
      <Card data-id="element-6965">
        <Table columns={columns} data={users} keyField="id" actions={user => <div className="flex justify-end space-x-2" data-id="element-6967">
              <button onClick={() => handleViewUser(user)} className="p-1 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700" title="View" data-id="element-6968">
                <EyeIcon className="h-5 w-5" data-id="element-6969" />
              </button>
              <button onClick={() => handleEditUser(user)} className="p-1 rounded-full text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30" title="Edit" data-id="element-6970">
                <EditIcon className="h-5 w-5" data-id="element-6971" />
              </button>
              <button onClick={() => handleDeleteUser(user)} className="p-1 rounded-full text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30" title="Delete" data-id="element-6972">
                <TrashIcon className="h-5 w-5" data-id="element-6973" />
              </button>
            </div>} data-id="element-6966" />
      </Card>
      {/* View User Modal */}
      <Modal isOpen={showViewModal} onClose={() => setShowViewModal(false)} title="User Details" size="md" data-id="element-6974">
        {selectedUser && <div className="space-y-4" data-id="element-6975">
            <div className="flex items-center" data-id="element-6976">
              <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 mr-3" data-id="element-6977">
                {getStatusIcon(selectedUser.status)}
              </div>
              <div data-id="element-6978">
                <h3 className="text-lg font-medium" data-id="element-6979">{selectedUser.fullName}</h3>
                <div className="flex items-center mt-1" data-id="element-6980">
                  {getStatusBadge(selectedUser.status)}
                  <span className="mx-2 text-gray-500 dark:text-gray-400" data-id="element-6981">
                    •
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-6982">
                    {selectedUser.role}
                  </span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 mt-4" data-id="element-6983">
              <div data-id="element-6984">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-6985">
                  Contact Information
                </h4>
                <div className="mt-2 space-y-2" data-id="element-6986">
                  <p data-id="element-6987">
                    <span className="font-medium" data-id="element-6988">Username:</span>{' '}
                    {selectedUser.username}
                  </p>
                  <p data-id="element-6989">
                    <span className="font-medium" data-id="element-6990">Email:</span>{' '}
                    {selectedUser.email}
                  </p>
                  <p data-id="element-6991">
                    <span className="font-medium" data-id="element-6992">Phone:</span>{' '}
                    {selectedUser.phone}
                  </p>
                </div>
              </div>
              <div data-id="element-6993">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-6994">
                  Account Details
                </h4>
                <div className="mt-2 space-y-2" data-id="element-6995">
                  <p data-id="element-6996">
                    <span className="font-medium" data-id="element-6997">Region:</span>{' '}
                    {selectedUser.region}
                  </p>
                  <p data-id="element-6998">
                    <span className="font-medium" data-id="element-6999">Last Login:</span>{' '}
                    {formatDate(selectedUser.lastLogin)}
                  </p>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4" data-id="element-7000">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2" data-id="element-7001">
                Quick Actions
              </h4>
              <div className="flex flex-wrap gap-2" data-id="element-7002">
                <button className="px-3 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-md text-sm" data-id="element-7003">
                  Reset Password
                </button>
                <button className="px-3 py-1 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 rounded-md text-sm" data-id="element-7004">
                  Lock Account
                </button>
                <button className="px-3 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded-md text-sm" data-id="element-7005">
                  View Activity
                </button>
              </div>
            </div>
          </div>}
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Confirm Deletion" footer={<>
            <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-7007">
              Cancel
            </button>
            <button onClick={confirmDelete} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700" data-id="element-7008">
              Delete
            </button>
          </>} data-id="element-7006">
        <p className="text-gray-700 dark:text-gray-300" data-id="element-7009">
          Are you sure you want to delete the user "{selectedUser?.fullName}"?
          This action cannot be undone.
        </p>
      </Modal>
    </div>;
};
export default UserList;